import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { SessionUser } from "../../shared/contracts.js";
import type { Role } from "../../shared/domain.js";
import { ClientApiError, isClientApiError, request } from "../api/http.js";

/**
 * Application-wide providers: the React Query client and the session.
 *
 * The session is one query — `GET /api/auth/session` — that every route guard
 * and page reads. Login, logout and password change all end by refreshing
 * that query, so the server's answer, never a client-side guess, decides who
 * is signed in, which role they hold and whether a temporary password still
 * has to be replaced.
 */

/**
 * The canonical `Role` union (`src/shared/domain.ts`) and `SessionUser` DTO
 * (`src/shared/contracts.ts`), re-exported for client code.
 */
export type { Role, SessionUser };

export type SessionStatus =
  "loading" | "authenticated" | "anonymous" | "unavailable";

export type LoginInput = Readonly<{ email: string; password: string }>;
export type ChangePasswordInput = Readonly<{
  currentPassword: string;
  newPassword: string;
}>;

export interface SessionContextValue {
  user: SessionUser | null;
  status: SessionStatus;
  /** Why the session could not be determined (`status === "unavailable"`). */
  error: unknown;
  login(input: LoginInput): Promise<SessionUser>;
  logout(): Promise<void>;
  /** Re-reads the session from the server and returns the current user. */
  refresh(): Promise<SessionUser | null>;
  changePassword(input: ChangePasswordInput): Promise<SessionUser>;
}

export const sessionQueryKey = ["auth", "session"] as const;

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Accepts either `{ user: SessionUser | null }` or a bare `SessionUser` body,
 * so the client stays correct whichever envelope the auth route settles on.
 */
function extractSessionUser(body: unknown): SessionUser | null {
  if (typeof body !== "object" || body === null) return null;
  if ("user" in body) {
    const candidate = (body as { user: unknown }).user;
    return isSessionUser(candidate) ? candidate : null;
  }
  return isSessionUser(body) ? body : null;
}

function isSessionUser(value: unknown): value is SessionUser {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate["id"] === "string" &&
    typeof candidate["role"] === "string" &&
    typeof candidate["displayName"] === "string"
  );
}

/**
 * Who the server says is signed in, or `null` for nobody. The probe answers
 * 200 `{ user: null }` to an anonymous visitor (no cookie, expired or revoked
 * session), so the sign-in page loads without a failed request; the 401
 * fallback stays so an older server build, mid rolling deploy, still reads as
 * anonymous rather than as an outage.
 */
export async function fetchSession(): Promise<SessionUser | null> {
  try {
    return extractSessionUser(await request<unknown>("/api/auth/session"));
  } catch (error) {
    if (isClientApiError(error) && error.status === 401) return null;
    throw error;
  }
}

/** Retries transient failures only; a definite 4xx answer is final. */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isClientApiError(error) && error.status >= 400 && error.status < 500)
    return false;
  return failureCount < 2;
}

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        staleTime: 30_000,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}

function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: sessionQueryKey,
    queryFn: fetchSession,
    staleTime: 5 * 60_000,
  });

  // A 401 from any other request means the session ended on the server
  // (expiry, deactivation, password reset). Reflect it at once so the route
  // guards send the person back to sign in instead of showing stale screens.
  useEffect(() => {
    const cache = queryClient.getQueryCache();
    return cache.subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "error") return;
      if (event.query.queryKey[0] === sessionQueryKey[0]) return;
      const error = event.action.error;
      if (isClientApiError(error) && error.status === 401) {
        queryClient.setQueryData(sessionQueryKey, null);
      }
    });
  }, [queryClient]);

  const refresh = useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: sessionQueryKey,
        queryFn: fetchSession,
        staleTime: 0,
      }),
    [queryClient],
  );

  const login = useCallback(
    async (input: LoginInput): Promise<SessionUser> => {
      await request<unknown>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(input),
      });
      const user = await refresh();
      if (!user) {
        throw new ClientApiError(
          401,
          "UNAUTHENTICATED",
          "Sign in did not start a session.",
        );
      }
      return user;
    },
    [refresh],
  );

  /**
   * Ends the session on the server first and only then forgets it locally.
   *
   * The cookie is HttpOnly, so the client cannot end a session by itself: if
   * the request fails, the server still holds a session valid for up to 12
   * hours, and the person — often at a shared dealership computer — must be
   * told rather than shown a login page that pretends they left. The one
   * exception is a 401: the session was already gone, which is the state
   * sign-out wanted.
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await request<unknown>("/api/auth/logout", { method: "POST" });
    } catch (error) {
      if (!(isClientApiError(error) && error.status === 401)) throw error;
    }
    // Drop every cached record so the next person at this device starts clean.
    await queryClient.cancelQueries();
    queryClient.removeQueries({
      predicate: (candidate) => candidate.queryKey[0] !== sessionQueryKey[0],
    });
    queryClient.getMutationCache().clear();
    queryClient.setQueryData(sessionQueryKey, null);
  }, [queryClient]);

  const changePassword = useCallback(
    async (input: ChangePasswordInput): Promise<SessionUser> => {
      await request<unknown>("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify(input),
      });
      const user = await refresh();
      if (!user) {
        throw new ClientApiError(
          401,
          "UNAUTHENTICATED",
          "Your session ended. Sign in again.",
        );
      }
      return user;
    },
    [refresh],
  );

  const status: SessionStatus = query.isPending
    ? "loading"
    : query.isError
      ? "unavailable"
      : query.data
        ? "authenticated"
        : "anonymous";

  const value = useMemo<SessionContextValue>(
    () => ({
      user: query.data ?? null,
      status,
      error: query.error,
      login,
      logout,
      refresh,
      changePassword,
    }),
    [query.data, query.error, status, login, logout, refresh, changePassword],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession must be used inside <AppProviders>");
  }
  return value;
}

export interface AppProvidersProps {
  children: ReactNode;
  /** Tests inject a fresh client per test; production builds one on mount. */
  queryClient?: QueryClient;
}

export function AppProviders({ children, queryClient }: AppProvidersProps) {
  const [client] = useState(() => queryClient ?? createAppQueryClient());
  return (
    <QueryClientProvider client={client}>
      <SessionProvider>{children}</SessionProvider>
    </QueryClientProvider>
  );
}
